import type {
  EnvironmentId,
  ProjectId,
  PullRequestActivity,
  PullRequestDetail,
  PullRequestRef,
} from "@cmmd-forge/contracts";
import { createContext, useContext, useCallback, useMemo, useState, type ReactNode } from "react";

import { usePrimaryEnvironmentDescriptor } from "~/environments/primary";
import { useSavedEnvironmentRuntimeStore } from "~/environments/runtime/savedEnvironmentRuntimeStore";
import { useProjectsAcrossEnvironments } from "~/hooks/useProjectsAcrossEnvironments";
import type { Project } from "~/types";
import type { PullRequestAgentSelection } from "../pullRequestSelectionContext";
import type {
  PullRequestHandoffAction,
  PullRequestHandoffFinding,
} from "./pullRequestHandoffPlanning";
import { usePullRequestHandoff } from "./usePullRequestHandoff";
import {
  compareOfferedCheckouts,
  matchingReadyRepositoryProjects,
  resolvePullRequestActingTarget,
  type PullRequestHandoffTargetSelection,
} from "./pullRequestHandoffTargets";
import {
  pullRequestHandoffEnvironmentLabels,
  pullRequestHandoffProjectKey as projectKey,
  type PullRequestHandoffPrimaryEnvironment,
  type PullRequestHandoffSavedRuntime,
} from "./pullRequestHandoffEnvironmentLabels";

export type HandoffController = {
  readonly sourceProject: Project;
  readonly targetProject: Project;
  readonly targets: ReadonlyArray<Project>;
  readonly environmentLabels: ReadonlyMap<EnvironmentId, string>;
  readonly currentThreadMatches: boolean;
  /** Whether a checkout is the reader's to choose here; false beside a thread. */
  readonly canPickTarget: boolean;
  readonly pendingAction: PullRequestHandoffAction | null;
  readonly selectTarget: (project: Project) => void;
  readonly run: (
    action: PullRequestHandoffAction,
    finding?: PullRequestHandoffFinding,
  ) => Promise<void>;
};

/** Where the panel is mounted: on its own page, or beside a thread. */
export type PullRequestHandoffContextKind = "page" | "thread";

const PullRequestHandoffContext = createContext<HandoffController | null>(null);

export function PullRequestHandoffProvider(props: {
  readonly detail: PullRequestDetail;
  readonly reference: PullRequestRef;
  readonly activity: PullRequestActivity | undefined;
  readonly environmentId: EnvironmentId;
  readonly selection: PullRequestAgentSelection | null;
  readonly context: PullRequestHandoffContextKind;
  readonly children: ReactNode;
}) {
  const projects = useProjectsAcrossEnvironments();
  const primary = usePrimaryEnvironmentDescriptor();
  const savedRuntimeById = useSavedEnvironmentRuntimeStore((state) => state.byId);
  const readyEnvironmentIds = pullRequestHandoffReadyEnvironmentIds({
    primary,
    savedRuntimeById,
  });
  const environmentLabels = pullRequestHandoffEnvironmentLabels({ primary, savedRuntimeById });
  const pullRequestLinkingEnvironmentIds = new Set<EnvironmentId>([
    ...(primary?.capabilities.threadPullRequestLinking === true ? [primary.environmentId] : []),
    ...Object.entries(savedRuntimeById).flatMap(([environmentId, runtime]) =>
      runtime.connectionState === "connected" &&
      runtime.authState === "authenticated" &&
      runtime.descriptor?.capabilities.threadPullRequestLinking === true
        ? [environmentId as EnvironmentId]
        : [],
    ),
  ]);
  const sourceProject = resolvePullRequestSourceProject({
    projects,
    canonicalRepositoryKey: pullRequestCanonicalRepositoryKey(props.detail),
    preferredEnvironmentId: props.environmentId,
    pullRequestProjectId: props.reference.projectId,
    readyEnvironmentIds,
  });
  const targets =
    sourceProject === undefined
      ? []
      : matchingReadyRepositoryProjects(sourceProject, projects, readyEnvironmentIds);
  return sourceProject === undefined || targets.length === 0 ? (
    props.children
  ) : (
    <ConnectedPullRequestHandoffProvider
      {...props}
      sourceProject={sourceProject}
      targets={targets}
      environmentLabels={environmentLabels}
      pullRequestLinkingEnvironmentIds={pullRequestLinkingEnvironmentIds}
    />
  );
}

export function pullRequestHandoffReadyEnvironmentIds(input: {
  readonly primary: PullRequestHandoffPrimaryEnvironment | null;
  readonly savedRuntimeById: Readonly<Record<EnvironmentId, PullRequestHandoffSavedRuntime>>;
}): ReadonlySet<EnvironmentId> {
  return new Set<EnvironmentId>([
    ...(input.primary?.capabilities.runtime?.executionPlane === true
      ? [input.primary.environmentId]
      : []),
    ...Object.entries(input.savedRuntimeById).flatMap(([environmentId, runtime]) =>
      runtime.connectionState === "connected" &&
      runtime.authState === "authenticated" &&
      runtime.descriptor?.capabilities.runtime?.executionPlane === true
        ? [environmentId as EnvironmentId]
        : [],
    ),
  ]);
}

export function resolvePullRequestSourceProject(input: {
  readonly projects: ReadonlyArray<Project>;
  readonly canonicalRepositoryKey: string | null;
  readonly preferredEnvironmentId: EnvironmentId;
  /**
   * The project the pull request was read under. A repository registered as several worktrees
   * gives every checkout the same canonical key, so the key on its own cannot say which checkout
   * the reader had open — it is built to collapse them. Only the id can, and only within the
   * environment that issued it, because ids are unique per environment and reused across them.
   */
  readonly pullRequestProjectId: ProjectId | undefined;
  readonly readyEnvironmentIds: ReadonlySet<EnvironmentId>;
}): Project | undefined {
  if (input.canonicalRepositoryKey === null) return undefined;
  // Sorted, not filtered into store order: every fallback below ends in a `[0]`, and on a
  // Command-scoped read — which is every read from the pull request route — the id match cannot
  // fire, so one of those `[0]`s decides the default. Store order there is the original defect.
  const candidates = input.projects
    .filter(
      (project) =>
        project.repositoryIdentity?.canonicalKey.toLowerCase() === input.canonicalRepositoryKey &&
        input.readyEnvironmentIds.has(project.environmentId),
    )
    .toSorted(compareOfferedCheckouts);
  // Only a checkout that is on disk can host a handoff — `matchingReadyRepositoryProjects` drops
  // every other source — so resolving to a missing one removes the Agent menu rather than acting
  // on the wrong project. A live sibling of the same repository is the better answer.
  const live = candidates.filter((project) => project.availability === "available");
  const preferred = live.filter(
    (project) => project.environmentId === input.preferredEnvironmentId,
  );
  // A pull request read from an environment that cannot execute (the Command authority) has no
  // id in any runtime's space, so it falls through to the repository key exactly as before.
  return (
    preferred.find((project) => project.id === input.pullRequestProjectId) ??
    preferred[0] ??
    live[0] ??
    candidates[0]
  );
}

export function pullRequestCanonicalRepositoryKey(
  detail: Pick<PullRequestDetail, "repository" | "url">,
): string | null {
  try {
    const host = new URL(detail.url).hostname.trim().toLowerCase();
    const repository = detail.repository
      .trim()
      .replace(/^\/+|\/+$/g, "")
      .toLowerCase();
    return host.length === 0 || repository.length === 0 ? null : `${host}/${repository}`;
  } catch {
    return null;
  }
}

function ConnectedPullRequestHandoffProvider(
  props: Parameters<typeof PullRequestHandoffProvider>[0] & {
    readonly sourceProject: Project;
    readonly targets: ReadonlyArray<Project>;
    readonly environmentLabels: ReadonlyMap<EnvironmentId, string>;
    readonly pullRequestLinkingEnvironmentIds: ReadonlySet<EnvironmentId>;
  },
) {
  const [selection, setSelection] = useState<PullRequestHandoffTargetSelection | null>(null);
  const canPickTarget = props.context === "page";
  const { pullRequestKey, targetProject } = resolvePullRequestActingTarget({
    canPickTarget,
    environmentId: props.environmentId,
    detail: props.detail,
    sourceProject: props.sourceProject,
    targets: props.targets,
    selection,
  });
  const handoff = usePullRequestHandoff({
    detail: props.detail,
    reference: props.reference,
    activity: props.activity,
    sourceProject: props.sourceProject,
    targetProject,
    selection: props.selection,
    canLinkPullRequest: props.pullRequestLinkingEnvironmentIds.has(targetProject.environmentId),
  });
  const selectTarget = useCallback(
    (project: Project) => setSelection({ pullRequestKey, targetKey: projectKey(project) }),
    [pullRequestKey],
  );
  const run = useCallback(
    (action: PullRequestHandoffAction, finding?: PullRequestHandoffFinding) =>
      handoff.run(action, targetProject, finding),
    [handoff, targetProject],
  );
  const controller = useMemo<HandoffController>(
    () => ({
      currentThreadMatches: handoff.currentThreadMatches,
      pendingAction: handoff.pendingAction,
      sourceProject: props.sourceProject,
      targetProject,
      targets: props.targets,
      canPickTarget,
      environmentLabels: props.environmentLabels,
      selectTarget,
      run,
    }),
    [
      canPickTarget,
      handoff.currentThreadMatches,
      handoff.pendingAction,
      props.environmentLabels,
      props.sourceProject,
      props.targets,
      run,
      selectTarget,
      targetProject,
    ],
  );
  return (
    <PullRequestHandoffContext.Provider value={controller}>
      {props.children}
    </PullRequestHandoffContext.Provider>
  );
}

export function usePullRequestHandoffController() {
  return useContext(PullRequestHandoffContext);
}
